import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@landr/maestro';
import { Trans } from '@lingui/macro';
import { RoutePaths } from 'types/enums';
import { copyToClipboard } from 'utils/copyToClipboard';
import { analytics } from 'utils/analytics';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import {
    InteractionSource,
    Relation,
    ShareMethod,
    AssetCategoryGroup,
    AssetCategory,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { useAbsoluteUrl } from 'hooks/useAbsoluteUrl/useAbsoluteUrl';
import { ShareModalSkeleton } from './ShareModal.Skeleton';
import { ShareModalError } from './ShareModal.Error';
import { ShareModalReady } from './ShareModal.Ready';

export const ShareModal: React.FC = (): JSX.Element => {
    const [creatorUrl, setCreatorUrl] = useState('');
    const [hasBeenCopied, setHasBeenCopied] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const {
        state: creatorState,
        closeShareModal,
        storeSession,
        dispatch,
    } = useCreatorContext();
    const {
        isShareModalOpen,
        samplesInSession,
        instrumentationSessionId,
        keySign,
        keyRoot,
        keyQuality,
        bpm,
    } = creatorState;

    const handleClose = (): void => {
        closeShareModal();
        dispatch({ type: 'newInstrumentationSessionId' });
        setTimeout(() => {
            setHasBeenCopied(false);
            setCreatorUrl('');
        }, 500);
    };

    const handleCopyToClipboard = useCallback((): void => {
        copyToClipboard(creatorUrl);

        setHasBeenCopied(true);

        analytics.trackAssetShared({
            relation: Relation.Owner,
            interactionSource: InteractionSource.CreatorAction,
            numberFilesInAsset: samplesInSession.size,
            shareMethod: ShareMethod.CopyLink,
            assetCategoryGroup: AssetCategoryGroup.Audio,
            assetCategory: AssetCategory.CreatorTrack,
            assetName: 'mixdown',
            assetId: instrumentationSessionId,
            assetBpm: bpm,
            assetKey: `${keyRoot} ${keyQuality} - ${keySign}`,
        });
    }, [
        samplesInSession,
        bpm,
        keySign,
        keyRoot,
        keyQuality,
        creatorUrl,
        instrumentationSessionId,
    ]);

    const [sessionGuid, setSessionGuid] = useState<string | null>(null);

    useEffect(() => {
        async function getSessionGuid(): Promise<void> {
            setIsFetching(true);
            const guid = await storeSession();

            if (guid) {
                setSessionGuid(guid);
            }

            setIsFetching(false);
        }

        if (isShareModalOpen) {
            getSessionGuid();
        }
    }, [isShareModalOpen, storeSession]);

    const getAbsoluteUrl = useAbsoluteUrl();

    const absoluteCreatorUrl = getAbsoluteUrl(
        `/${RoutePaths.CreatorBeatMaker}/${sessionGuid}`,
    );

    useEffect(() => {
        setCreatorUrl(absoluteCreatorUrl);
        setHasBeenCopied(false);
    }, [absoluteCreatorUrl]);

    return (
        <Modal isOpen={isShareModalOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>
                    <Trans>Share your track</Trans>
                </Modal.Title>
            </Modal.Header>
            <Modal.Content>
                <Modal.Section>
                    {isFetching && <ShareModalSkeleton />}
                    {!isFetching && !creatorUrl && <ShareModalError />}
                    {!isFetching && creatorUrl && (
                        <ShareModalReady
                            creatorUrl={creatorUrl}
                            hasBeenCopied={hasBeenCopied}
                            onClick={handleCopyToClipboard}
                        />
                    )}
                </Modal.Section>
            </Modal.Content>
            <Modal.Footer></Modal.Footer>
        </Modal>
    );
};
